// import { useFlag } from '@unleash/proxy-client-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { useIntroducingBrokerById } from '@pages/IntroducingBroker/hooks/useIntroducingBrokerById'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useIBCampaignsResultsReadContext } from '@context/IBCampaignsResultsContext'

import { EditFileIcon } from '@ui/icons/EditFile.icon'
import { ExchangeIcon } from '@ui/icons/Exchange.icon'
import { FilesIcon } from '@ui/icons/Files.icon'
import { LayersIcon } from '@ui/icons/Layers.icon'
import { LayoutIcon } from '@ui/icons/Layout.icon'
import { LinkIcon } from '@ui/icons/Link.icon'
import { StarIcon } from '@ui/icons/Star.icon'
import { TrophyIcon } from '@ui/icons/Trophy.icon'
import { VoiceIcon } from '@ui/icons/Voice.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'

import { isTickmillPartner } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { TickmillProductType } from '@model/TickmillProductType'

import { usePAMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/usePAMenu'
import { useProfileMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useProfileMenu'
import { useShowContactSupport } from '@shared/components/Navigation/menuSections/topLevelMenus/useShowContactSupport'

import { MenuSection } from '../useMenuSections'

export const useIBMenu = (): MenuSection[] => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { ibCampaignsResults } = useIBCampaignsResultsReadContext()
  const showContactSupport = useShowContactSupport()
  const profile = useProfileMenu()
  const { data: introducingBroker } = useIntroducingBrokerById()
  const { isIBVisible, isMultiTierVisible, isRebatesVisible, isLinkHubVisible } =
    useIBRoutesConditions()
  const isDashboardPoCActive = true
  const isMobile = useWindowResize()
  const paymentAgent = usePAMenu()

  return useMemo(() => {
    if (!isIBVisible) return []

    const sharedProps = {
      icon: LayoutIcon,
      products: [TickmillProductType.CFD],
    }

    if (!account?.clientIntroducingBroker?.id) {
      return []
    }

    return [
      {
        ...sharedProps,
        id: 'ib-dashboard',
        label: t('Tabs.Dashboard'),
        path: CommonPaths.IntroducingBrokerDashboard,
        condition: isDashboardPoCActive,
      },
      {
        ...sharedProps,
        id: 'ib-rebates',
        label: t('Tabs.Rebates'),
        path: '/dashboard/introducing-broker/rebates/clients',
        condition: isRebatesVisible,
        icon: ExchangeIcon,
      },
      {
        ...sharedProps,
        id: 'ib-wallets',
        label: t('Tabs.Wallets'),
        icon: WalletIcon,
        path: CommonPaths.IntroducingBrokerWallets,
      },
      {
        ...sharedProps,
        id: 'ib-reports',
        label: t('Tabs.Reports'),
        path: '/dashboard/introducing-broker/reports/clients',
        icon: FilesIcon,
      },
      {
        ...sharedProps,
        id: 'ib-legal-documents',
        label: t('Tabs.Legal Documents'),
        icon: EditFileIcon,
        path: '/dashboard/introducing-broker/legal-documents',
      },
      {
        ...sharedProps,
        id: 'ib-multi-tier',
        label: t('Tabs.Multi-Tier'),
        path: '/dashboard/introducing-broker/multi-tier/commission-plans',
        icon: LayersIcon,
        condition: isMultiTierVisible,
      },
      {
        ...sharedProps,
        id: 'ib-referral-materials',
        label: t('Tabs.Referral Materials'),
        icon: VoiceIcon,
        path: '/dashboard/introducing-broker/referral-materials',
        condition: !isTickmillPartner(account),
      },
      {
        ...sharedProps,
        id: 'ib-contest',
        label: t('Tabs.IB Contest'),
        icon: TrophyIcon,
        path: '/dashboard/introducing-broker/ib-contest',
        condition: !!ibCampaignsResults.length,
      },
      {
        ...sharedProps,
        id: 'ib-bonus-program',
        label: t('Tabs.Bonus Program'),
        condition: introducingBroker?.loyaltyProgramEnabled,
        icon: StarIcon,
        path: '/dashboard/introducing-broker/bonus-program',
      },
      {
        ...sharedProps,
        id: 'ib-link-hub',
        label: t('Tabs.Link Hub'),
        icon: LinkIcon,
        path: '/dashboard/introducing-broker/link-hub',
        condition: isLinkHubVisible,
      },
      paymentAgent,
      ...(isMobile ? [profile] : []),
      ...(isMobile ? [showContactSupport] : []),
    ]
  }, [
    account,
    ibCampaignsResults.length,
    introducingBroker?.loyaltyProgramEnabled,
    isDashboardPoCActive,
    isIBVisible,
    isMultiTierVisible,
    isRebatesVisible,
    isLinkHubVisible,
    paymentAgent,
    showContactSupport,
    profile,
    isMobile,
    t,
  ])
}
